import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, Dropdown, Input, Menu } from "antd";
import { LogoutOutlined, SearchOutlined, SettingOutlined } from "@ant-design/icons";
import { geturl } from "../../../../common/api/SafeClient";
import { User } from "../../../../types/UserGroup";
import { useTailSpend } from "../TailSpendContext";
import { stageKindLabel } from "../model/lifecycle";
import classes from "./TailSpendHeader.module.less";

interface Props {
    me: Partial<User>;
}

interface ResultItem {
    type: "DEAL" | "SUPPLIER";
    title: string;
    sub: string;
    onPick: () => void;
    keyId: string;
}

const dealPath = (id: string) => `/superadmin/demo/tail-spend/negotiations/${id}`;
const suppliersPath = "/superadmin/demo/tail-spend/suppliers";

export const TailSpendHeader: React.FC<Props> = ({ me }) => {
    const navigate = useNavigate();
    const { workspace } = useTailSpend();
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    const items: ResultItem[] = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const dealItems: ResultItem[] = workspace.deals.map((d) => ({
            type: "DEAL",
            title: d.supplierName,
            sub: `${d.categoryName} · ${d.statusChip.label || stageKindLabel(d.state)}`,
            onPick: () => navigate(dealPath(d.id)),
            keyId: `d-${d.id}`,
        }));
        const supItems: ResultItem[] = workspace.suppliers.map((s) => {
            const cat = workspace.categories.find((c) => c.id === s.categoryId);
            return {
                type: "SUPPLIER",
                title: s.name,
                sub: `${cat?.name ?? ""} · $${s.annualSpend.amount.toLocaleString()}`,
                onPick: () => {
                    if (s.currentDealId) navigate(dealPath(s.currentDealId));
                    else navigate(suppliersPath);
                },
                keyId: `s-${s.id}`,
            };
        });
        return [...dealItems, ...supItems]
            .filter((r) => r.title.toLowerCase().includes(q) || r.sub.toLowerCase().includes(q))
            .slice(0, 8);
    }, [query, workspace.deals, workspace.suppliers, workspace.categories, navigate]);

    const handlePick = (item: ResultItem) => {
        item.onPick();
        setQuery("");
        setOpen(false);
    };

    const userMenu = (
        <Menu>
            <Menu.Item key="hi">Hi {me.firstName} 👋</Menu.Item>
            <Menu.Item key="prefs" icon={<SettingOutlined />}>
                <Link to="/preferences"> My preferences</Link>
            </Menu.Item>
            <Menu.Item key="logout" icon={<LogoutOutlined />}>
                <a href={geturl("/api/logout")}>Log out</a>
            </Menu.Item>
        </Menu>
    );

    return (
        <div className={classes.header}>
            <Link to="/" className={classes.logo}>
                <img src="/infinity-loop-logo-horz-color-wt.svg" alt="logo" />
            </Link>

            <div ref={wrapRef} className={classes.searchWrap}>
                <Input
                    className={classes.search}
                    prefix={<SearchOutlined />}
                    placeholder="Search suppliers, deals, POs…"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => query && setOpen(true)}
                />
                {open && query && (
                    <div className={classes.results}>
                        {items.length === 0 ? (
                            <div className={classes.empty}>No matches for that search.</div>
                        ) : (
                            items.map((r) => (
                                <button
                                    key={r.keyId}
                                    className={classes.resultItem}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handlePick(r)}
                                >
                                    <span className={classes.badge}>
                                        {r.type === "DEAL" ? "DEAL" : "SUP"}
                                    </span>
                                    <div className={classes.body}>
                                        <div className={classes.title}>{r.title}</div>
                                        <div className={classes.sub}>{r.sub}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>

            <Dropdown overlay={userMenu}>
                <Avatar
                    className={classes.avatar}
                    shape="square"
                    size={64}
                    src={me.avatar || "/base-avatar.png"}
                />
            </Dropdown>
        </div>
    );
};
